import React, { FC, memo } from 'react';

import { RichText } from '@montana/shared/rich-text';
import { IM01HeroBlock } from '@montana/models/generated/cms.interface';
import { TabletAndLarger, MobileAndTabletPortrait } from '@montana/shared/responsive';
import cx from 'clsx';
import { Media } from '@montana/shared/media';
import { epiMediaRatioToEnum } from '@montana/helpers/epi-mapping.helper';
import { Link } from '@montana/shared/link';
import { extractCTALink } from '@montana/helpers/rich-text.helper';
import { useRouter } from '@montana/containers/router';
import { Appear } from '@montana/shared/appear';
import { Breadcrumbs } from '@montana/shared/breadcrumbs';
import { Container } from '@montana/shared/container';
import { IFEBLock } from '@montana/models/dev';

const HERO_FIRST_APPEAR_DELAY_MS = 400;

export type HeroLayouts = 'landscape' | 'square' | 'no-image' | 'portrait' | 'wide';

interface IHeroSizes {
    'landscape'?: string;
    'square'?: string;
    'no-image'?: string;
    'portrait'?: string;
    'wide'?: string;
}

const mediaSizes: IHeroSizes = {
    square: [
        '(min-width: 1680px) 630px',
        '(min-width: 1367px) 551px',
        '(min-width: 1223px) 630px',
        '(min-width: 811px) 52vw',
        '(min-width: 480px) calc(100vw - 80px)',
        '100vw'
    ].join(', '),
    portrait: [
        '(min-width: 1680px) 470px',
        '(min-width: 1367px) 411px',
        '(min-width: 811px) 30vw',
        '(min-width: 480px) 30vw',
        '80vw'
    ].join(', '),
    landscape: [
        '(min-width: 1680px) 835px',
        '(min-width: 1367px) 731px',
        '(min-width: 811px) 52vw',
        '(min-width: 480px) calc(100vw - 80px)',
        '100vw'
    ].join(', '),
    wide: '100vw',
}

export const M01HeroBlock: FC<IM01HeroBlock> = memo((props: IM01HeroBlock & IFEBLock) => {
    const {
        content,
        backgroundColorHex,
        image,
        video,
        mediaDisplayRatio = 'square',
        useMediaAsBackground,
        isFirstOnPage,
    } = props;

    const { isFirstRendering } = useRouter();
    const heroLayout: HeroLayouts = ((image || video)
        ? useMediaAsBackground ? 'wide' : mediaDisplayRatio
        : 'no-image') as HeroLayouts;

    const renderMedia = () => (
        <div className="hero-block__media-container">
            <Link
                className="hero-block__media"
                to={extractCTALink(content) || undefined}>
                <Media
                    image={image}
                    video={video}
                    videoAutoplay
                    videoPlayInline
                    videoMuted
                    videoLoop
                    mediaRatio={epiMediaRatioToEnum(mediaDisplayRatio)}
                    placeholder={heroLayout !== 'wide'  }
                    animate={true}
                    sizes={mediaSizes[heroLayout]}
                    eager={isFirstOnPage}
                />
            </Link>
        </div>
    );

    return (
        <>
            <section className={`hero-block hero-block--${heroLayout}`}>
                <div className="hero-block__bg" style={{
                    backgroundColor: backgroundColorHex,
                    animationDelay: isFirstRendering ? `${HERO_FIRST_APPEAR_DELAY_MS}ms` : ''
                }}></div>
                <div className="hero-block__content">
                    {useMediaAsBackground && (
                        <TabletAndLarger>
                            <Media
                                image={image}
                                video={video}
                                videoAutoplay
                                videoPlayInline
                                videoMuted
                                videoLoop
                                className="hero-block__content-media-bg"
                                mediaRatio={false}
                                placeholder={heroLayout !== 'wide'}
                                animate={true}
                                sizes="100vw"
                                eager={isFirstOnPage}
                            />
                        </TabletAndLarger>
                    )}
                    {content && <RichText text={content} className={cx('hero-block__rich-text')} appearDelay={isFirstRendering ? HERO_FIRST_APPEAR_DELAY_MS : 0} />}
                    {!!(image || video) && (useMediaAsBackground ? (
                        <MobileAndTabletPortrait>
                            {renderMedia()}
                        </MobileAndTabletPortrait>
                    ) : (
                        <>
                            {renderMedia()}
                        </>
                    ))}
                    {!(useMediaAsBackground || !(image || video)) && (
                        <Appear delay={isFirstRendering ? HERO_FIRST_APPEAR_DELAY_MS : 0} className={`hero-block__breadcrumbs hero-block__breadcrumbs--${heroLayout}`}>
                            <Breadcrumbs />
                        </Appear>
                    )}
                </div>
            </section >
            {(useMediaAsBackground || !(image || video)) && (
                <Appear delay={isFirstRendering ? HERO_FIRST_APPEAR_DELAY_MS : 0} className={`hero-block__breadcrumbs hero-block__breadcrumbs--${heroLayout}`}>
                    <Container><Breadcrumbs /></Container>
                </Appear>
            )}
        </>
    );
});
